import React, { useState } from 'react';
import { Percent, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Advisor Rate Backfill — one-off operational panel.
 *
 * Re-applies `discount_type` to user records that were missed by the
 * automatic flow (most importantly: advisor-introduced clients whose
 * registration token tagged the access_request but didn't propagate to
 * the user record under the old register flow).
 *
 * Always offers a DRY RUN first; the apply step is gated by an explicit
 * confirm so an admin can never run it accidentally.
 */
export function AdvisorDiscountBackfillPanel({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);

  const fetchBackfill = async (dryRun) => {
    const setter = dryRun ? setLoading : setApplying;
    setter(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/discount-backfill/run?dry_run=${dryRun ? 'true' : 'false'}`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Backfill failed (${resp.status})`);
      }
      const data = await resp.json();
      setReport(data);
      if (!dryRun) {
        toast.success(`Backfill applied — ${data.advisor_partner.length} Partner, ${data.advisor_client.length} Preferred`);
        onApplied?.();
      }
    } catch (e) {
      toast.error(e.message || 'Backfill failed');
    } finally {
      setter(false);
    }
  };

  const apply = async () => {
    if (!report) { await fetchBackfill(true); return; }
    const total = (report.advisor_partner?.length || 0) + (report.advisor_client?.length || 0);
    if (total === 0) {
      toast.info('Nothing to back-fill.');
      return;
    }
    if (!window.confirm(`Apply discount backfill to ${total} user(s)? This change will be recorded in the audit log.`)) {
      return;
    }
    await fetchBackfill(false);
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-4" data-testid="advisor-discount-backfill-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
        data-testid="advisor-discount-backfill-toggle"
      >
        <div className="flex items-center gap-2 text-gray-200">
          <Percent className="w-4 h-4 text-emerald-400" />
          <span className="font-medium">Trusted Professional Rate Backfill</span>
          <span className="text-xs text-gray-400">Re-apply Partner / Preferred Client rates to missed user records</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchBackfill(true)}
              disabled={loading || applying}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm rounded-lg border border-gray-600 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="advisor-discount-backfill-dry-run"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Run dry-run preview
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={loading || applying || !report}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg disabled:opacity-50 flex items-center gap-1.5"
              data-testid="advisor-discount-backfill-apply"
              title={!report ? 'Run dry-run first' : ''}
            >
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Apply changes
            </button>
          </div>

          {report && (
            <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3 text-xs text-gray-300" data-testid="advisor-discount-backfill-report">
              <div className="font-semibold text-gray-100 mb-2">
                {report.dry_run ? 'Dry-run result' : 'Applied'} — Partner: {report.advisor_partner.length} · Preferred: {report.advisor_client.length}
              </div>
              {report.advisor_partner.length === 0 && report.advisor_client.length === 0 ? (
                <p className="text-gray-400">No users need a rate adjustment.</p>
              ) : (
                <div className="space-y-2">
                  {report.advisor_partner.length > 0 && (
                    <div>
                      <div className="text-emerald-400 font-medium mb-0.5">Partner Rate (20% off)</div>
                      <ul className="list-disc list-inside text-gray-400 space-y-0.5">
                        {report.advisor_partner.map((u) => (
                          <li key={u.user_id}>{u.email_redacted} · {u.user_id}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {report.advisor_client.length > 0 && (
                    <div>
                      <div className="text-amber-400 font-medium mb-0.5">Preferred Client Rate (10% off)</div>
                      <ul className="list-disc list-inside text-gray-400 space-y-0.5">
                        {report.advisor_client.map((u) => (
                          <li key={u.user_id}>{u.email_redacted} · {u.user_id}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
